import React, { useState, useEffect, useMemo } from "react";
import { IonModal, IonIcon } from "@ionic/react";
import { chevronBackOutline, chevronForwardOutline, closeOutline } from "ionicons/icons";

interface MonthPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (monthISO: string) => void;
  /** YYYY-MM – calendar opens showing this month */
  initialMonth?: string;
  /** YYYY-MM – earliest selectable month */
  minMonth?: string;
  /** YYYY-MM – latest selectable month */
  maxMonth?: string;
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const toMonthISO = (year: number, month: number) =>
  `${year}-${String(month + 1).padStart(2, "0")}`;

const MonthPicker: React.FC<MonthPickerProps> = ({
  isOpen,
  onClose,
  onSelect,
  initialMonth,
  minMonth,
  maxMonth,
}) => {
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (initialMonth) {
        const [year] = initialMonth.split("-").map(Number);
        setViewYear(year);
        setSelected(initialMonth);
      } else {
        setViewYear(new Date().getFullYear());
        setSelected(null);
      }
    }
  }, [isOpen, initialMonth]);

  const todayMonthISO = useMemo(() => {
    const today = new Date();
    return toMonthISO(today.getFullYear(), today.getMonth());
  }, []);

  const handleMonthClick = (monthIndex: number) => {
    const monthISO = toMonthISO(viewYear, monthIndex);
    if (minMonth && monthISO < minMonth) return;
    if (maxMonth && monthISO > maxMonth) return;
    setSelected(monthISO);
  };

  const handleGo = () => {
    if (selected) {
      onSelect(selected);
    }
  };

  const prevYear = () => setViewYear(viewYear - 1);
  const nextYear = () => setViewYear(viewYear + 1);

  const formatDisplay = (iso: string) => {
    const [year, month] = iso.split("-").map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      className="month-picker-modal"
      style={
        {
          "--width": "400px",
          "--max-width": "95vw",
          "--height": "auto",
          "--border-radius": "24px",
          "--backdrop-opacity": "0.5",
          "--box-shadow": "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        } as React.CSSProperties
      }
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "24px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
        }}
      >
        {/* Header with Gradient */}
        <div
          style={{
            background: "linear-gradient(135deg, #01b7c4 0%, #00acd7 100%)",
            padding: "28px 28px 40px",
            position: "relative",
          }}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.35)";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <IonIcon
              icon={closeOutline}
              style={{ color: "white", fontSize: "20px" }}
            />
          </button>

          {/* Title */}
          <div style={{ textAlign: "center" }}>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: "white",
                margin: "0 0 8px",
                letterSpacing: "-0.5px",
                fontFamily: "Figtree, sans-serif",
              }}
            >
              Go to Month
            </h2>
            <p
              style={{
                color: "rgba(255, 255, 255, 0.9)",
                fontSize: "14px",
                margin: 0,
                fontFamily: "Figtree, sans-serif",
              }}
            >
              {selected ? formatDisplay(selected) : "Select a month to jump to"}
            </p>
          </div>
        </div>

        {/* Content Area */}
        <div
          style={{
            background: "#ffffff",
            marginTop: "-20px",
            borderRadius: "24px 24px 0 0",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Year Navigation */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "20px 24px 12px",
            }}
          >
            <button
              onClick={prevYear}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#e6f9fc";
                e.currentTarget.style.borderColor = "#05acd9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#f8fafc";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
            >
              <IonIcon
                icon={chevronBackOutline}
                style={{ color: "#05acd9", fontSize: "18px" }}
              />
            </button>
            <span
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#1e293b",
                fontFamily: "Figtree, sans-serif",
              }}
            >
              {viewYear}
            </span>
            <button
              onClick={nextYear}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#e6f9fc";
                e.currentTarget.style.borderColor = "#05acd9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#f8fafc";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
            >
              <IonIcon
                icon={chevronForwardOutline}
                style={{ color: "#05acd9", fontSize: "18px" }}
              />
            </button>
          </div>

          {/* Month Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "12px",
              padding: "0 24px 16px",
            }}
          >
            {MONTHS.map((month, index) => {
              const monthISO = toMonthISO(viewYear, index);
              const isToday = monthISO === todayMonthISO;
              const isSelected = monthISO === selected;
              const disabled =
                (!!minMonth && monthISO < minMonth) ||
                (!!maxMonth && monthISO > maxMonth);

              return (
                <button
                  key={index}
                  onClick={() => !disabled && handleMonthClick(index)}
                  disabled={disabled}
                  style={{
                    height: "48px",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    fontWeight: isSelected ? 700 : isToday ? 700 : 400,
                    borderRadius: "12px",
                    border: "none",
                    cursor: disabled ? "default" : "pointer",
                    transition: "all 0.15s ease",
                    fontFamily: "Figtree, sans-serif",
                    color: disabled
                      ? "#d1d5db"
                      : isSelected
                      ? "#ffffff"
                      : isToday
                      ? "#05acd9"
                      : "#374151",
                    background: isSelected
                      ? "linear-gradient(135deg, #01b7c4 0%, #00acd7 100%)"
                      : "transparent",
                    boxShadow: isSelected
                      ? "0 4px 12px rgba(5, 172, 217, 0.3)"
                      : "none",
                  }}
                >
                  {month}
                </button>
              );
            })}
          </div>

          {/* Go Button */}
          <div
            style={{
              padding: "16px 24px 24px",
              borderTop: "1px solid #f1f5f9",
              background:
                "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            }}
          >
            <button
              onClick={handleGo}
              disabled={!selected}
              style={{
                width: "100%",
                height: "48px",
                borderRadius: "30px",
                border: "none",
                fontSize: "17px",
                fontWeight: 600,
                color: "white",
                cursor: selected ? "pointer" : "default",
                fontFamily: "Figtree, sans-serif",
                letterSpacing: "-0.3px",
                transition: "all 0.2s ease",
                background: selected
                  ? "linear-gradient(135deg, #01b7c4 0%, #00acd7 100%)"
                  : "#cbd5e1",
                boxShadow: selected
                  ? "0 4px 16px rgba(5, 172, 217, 0.3)"
                  : "none",
                opacity: selected ? 1 : 0.6,
              }}
            >
              Go
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .month-picker-modal {
          --ion-backdrop-opacity: 0.5;
        }
      `}</style>
    </IonModal>
  );
};

export default MonthPicker;
